import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'

import { useHttpClient } from '@/modules/shared/hooks/use-http-client'
import { useAuthRedirect } from '@/modules/shared/hooks/use-auth-redirect'
import { authEmailService } from '../services/auth-email.service'
import { useLoginStore } from '../stores/login.store'
import {
  type AuthEmailSchema,
  authEmailSchema
} from '../schemas/auth-email.schema'
import { emailStorage } from '../../../utils/email-storage.util'

export function useAuthEmail() {
  const navigate = useNavigate()
  const { setEmail, setCompanies } = useLoginStore()
  const { handleUnauthorized } = useAuthRedirect()
  const [rememberEmail, setRememberEmail] = useState(false)

  const form = useForm<AuthEmailSchema>({
    resolver: zodResolver(authEmailSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  })

  const httpClient = useHttpClient(
    authEmailService,
    {
      onSuccess: (response) => {
        if (response.status === 200) {
          const companies = JSON.parse(response.body)
          setCompanies(companies)
          navigate('/confirmar-senha')
        } else if (response.status === 401) {
          handleUnauthorized()
        }
      },
      showToast: true,
			showSucessToast: false,
    }
  )

  useEffect(() => {
    const loadSavedEmail = async () => {
      const { email, remember } = await emailStorage.getEmail()
      if (email && remember) {
        form.setValue('email', email)
        setRememberEmail(true)
      }
    }

    loadSavedEmail()
  }, [form])

  const submitAuthEmail = useCallback(async (data: AuthEmailSchema) => {
    setEmail(data.email)

    await emailStorage.saveEmail(data.email, rememberEmail)

    try {
      await httpClient.execute({ payload: data })
    } catch (error) {
      console.error('Erro no envio:', error)
    }
  }, [setEmail, rememberEmail, httpClient])

  const handleRememberChange = useCallback((checked: boolean) => {
    setRememberEmail(checked)

    if (!checked) {
      emailStorage.clearEmail()
    }
  }, [])

  return {
    form,
    isLoading: httpClient.isLoading,
    error: httpClient.error,
    submitAuthEmail,
    rememberEmail,
    setRememberEmail: handleRememberChange,
  }
}